"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  UserAccountMenu,
  type UserAccountMenuUser,
} from "./user-account-menu";

export interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
}

export interface AppSidebarProps {
  /** Application name shown in the header */
  appName: string;
  /** Optional logo element shown before appName */
  logo?: React.ReactNode;
  /** Navigation items */
  items: NavItem[];
  /** Current user info for the account menu */
  user?: UserAccountMenuUser;
  /** Called when user clicks Sign out */
  onLogout?: () => void;
  /** Currently active route href (for highlighting) */
  activeHref?: string;
  /** Whether the sidebar can collapse. Default: true */
  collapsible?: boolean;
  /**
   * Router Link component. Each app passes its own (e.g. react-router-dom's Link).
   * Must accept `to`, `className`, and `children` props.
   */
  LinkComponent: React.ComponentType<{
    to: string;
    className?: string;
    children: React.ReactNode;
  }>;
  className?: string;
}

export function AppSidebar({
  appName,
  logo,
  items,
  user,
  onLogout,
  activeHref,
  collapsible = true,
  LinkComponent,
  className,
}: AppSidebarProps) {
  const [collapsed, setCollapsed] = React.useState(false);
  const isCollapsed = collapsible && collapsed;

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-slot="app-sidebar"
        className={cn(
          "relative flex h-screen flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200",
          isCollapsed ? "w-16" : "w-64",
          !collapsible && "w-56",
          className
        )}
      >
        {/* ── Header ──────────────────────────────────────── */}
        <div className="flex h-14 items-center gap-3 border-b border-sidebar-border px-4">
          {logo && <div className="shrink-0">{logo}</div>}
          {!isCollapsed && (
            <span className="truncate text-sm font-semibold">{appName}</span>
          )}
        </div>

        {/* ── Navigation ──────────────────────────────────── */}
        <nav className="flex-1 overflow-y-auto p-2">
          <ul className="flex flex-col gap-1">
            {items.map((item) => {
              const isActive = activeHref === item.href;
              const Icon = item.icon;

              const linkContent = (
                <LinkComponent
                  to={item.href}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
                    "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                    isActive && "bg-sidebar-accent text-sidebar-accent-foreground inset-ring-1 inset-ring-sidebar-ring",
                    isCollapsed && "justify-center px-0"
                  )}
                >
                  <Icon className="size-4 shrink-0" />
                  {!isCollapsed && (
                    <>
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge != null && (
                        <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-sidebar-primary px-1.5 text-xs font-medium text-sidebar-primary-foreground">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </LinkComponent>
              );

              return (
                <li key={item.href}>
                  {isCollapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                      <TooltipContent side="right" sideOffset={8}>
                        <p>
                          {item.label}
                          {item.badge != null && ` (${item.badge})`}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    linkContent
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* ── Account Menu ────────────────────────────────── */}
        {user && onLogout && (
          <>
            <Separator className="bg-sidebar-border" />
            <div className="p-2">
              <UserAccountMenu
                user={user}
                onLogout={onLogout}
                collapsed={isCollapsed}
              />
            </div>
          </>
        )}

        {/* ── Collapse Toggle ─────────────────────────────── */}
        {collapsible && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setCollapsed((prev) => !prev)}
            className="absolute -right-3 top-[18px] z-20 size-6 rounded-full border bg-background shadow-sm"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <ChevronRight className="size-3" />
            ) : (
              <ChevronLeft className="size-3" />
            )}
          </Button>
        )}
      </aside>
    </TooltipProvider>
  );
}
